// src/pages/contentManagement/streaming/youtubeCreds.js
import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Input } from "antd";
import { FaYoutube } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { ROLES, getStoredRoleNormalized } from "../../../auth/permissions";

// ─── Constants ─────────────────────────────────────────────────────────────────
// Only head_admin can write credentials.
// All other roles with streaming access see this section as read-only.
const CREDENTIALS_WRITE_ROLES = [ROLES.HEAD_ADMIN];

function YoutubeCredentials({ youtube, onSaveSuccess, onDeleteSuccess, showNotification }) {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const role = getStoredRoleNormalized();
  const canWrite = CREDENTIALS_WRITE_ROLES.includes(role);

  // ── Derived state — always in sync with backend via youtube prop ───────────
  // No local state for these — driven entirely by what the GET response returns.
  const channelId = youtube?.channelId || "";

  // ── Submit channel ID ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    async (values) => {
      setIsSubmitting(true);
      try {
        const token = await getAuthToken();
        const payload = { channelId: values.channelId.trim() };
        const response = await axios.patch(API_ENDPOINTS.YOUTUBE_SETTINGS_CREDENTIALS, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification("YouTube channel ID saved successfully.", "success");
        onSaveSuccess(response.data);
      } catch (error) {
        console.error("Error saving YouTube credentials:", error);
        showNotification(error.response?.data?.message || "Failed to save channel ID. Please try again.", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSaveSuccess, showNotification]
  );

  // ── Delete channel ID ──────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      const token = await getAuthToken();
      await axios.delete(API_ENDPOINTS.YOUTUBE_SETTINGS_CREDENTIALS, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification("YouTube channel configuration cleared successfully.", "success");
      onDeleteSuccess();
    } catch (error) {
      console.error("Error deleting YouTube credentials:", error);
      showNotification(error.response?.data?.message || "Failed to clear channel configuration. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  }, [onDeleteSuccess, showNotification]);

  useEffect(() => {
    form.setFieldsValue({ channelId: youtube?.channelId || "" });
  }, [youtube, form]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="strm-upload-section">
      <div className="strm-section-header">
        <FaYoutube className="strm-section-icon" />
        <div>
          <h3 className="strm-section-title">YouTube Channel</h3>
          <p className="strm-section-subtitle">
            {canWrite
              ? "Enter your church's YouTube channel ID. This is used to fetch sermons, playlists, and detect livestreams in the mobile app."
              : "YouTube channel configuration is managed by the Head Administrator."}
          </p>
        </div>
      </div>

      {/* ── Form ─────────────────────────────────────────────────────────── */}
      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ channelId }} className="strm-credentials-form">
        <Form.Item
          label="YouTube Channel ID"
          name="channelId"
          rules={[{ required: true, message: "Please enter your YouTube channel ID" }]}
          extra="Starts with UC — found in your YouTube channel's advanced settings."
        >
          <Input className="strm-upload-input" placeholder="UCxxxxxxxxxxxxxxxxx" disabled={!canWrite || Boolean(channelId)} />
        </Form.Item>

        {canWrite && (
          <>
            {!channelId ? (
              <Form.Item>
                <Button className="strm-form-submit-btn" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
                  Save Channel ID
                </Button>
              </Form.Item>
            ) : (
              <Form.Item>
                <Button danger loading={isDeleting} disabled={isDeleting} onClick={handleDelete}>
                  Delete Channel ID
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </div>
  );
}

export default YoutubeCredentials;
